import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { premiumPlan, premiumStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Crown, Check, Star } from "lucide-react";

const FEATURES = [
  ["Your daily Panchangam with Current Hora & sun-path", "वर्तमान होरा एवं सूर्य-पथ सहित आपका दैनिक पंचांगम्"],
  ["Birth chart (D1/D9) & Vimshottari Dasha timeline", "जन्म कुंडली (D1/D9) एवं विम्शोत्तरी दशा समयरेखा"],
  ["Tarabala & Chandrashtama alerts tuned to you", "आपके अनुरूप ताराबल एवं चन्द्राष्टम चेतावनी"],
  ["Save family profiles, locations & a Japa counter", "परिवार प्रोफ़ाइल, स्थान एवं जप गणक सुरक्षित करें"],
];

// Prominent Panchangam Premium banner for the homepage & /panchang.
// Hides itself for users who already have premium.
export default function PremiumBanner({ className = "" }) {
  const { user } = useAuth();
  const { pick } = useLang();
  const [price, setPrice] = useState(99);
  const [active, setActive] = useState(false);

  useEffect(() => {
    premiumPlan().then((p) => setPrice(p.price || 99)).catch(() => {});
    if (user) premiumStatus().then((s) => setActive(!!s.active)).catch(() => {});
  }, [user]);

  if (active) return null;

  return (
    <section className={`mx-auto max-w-6xl px-5 md:px-8 ${className}`} data-testid="premium-banner">
      <div className="relative overflow-hidden rounded-2xl border border-[var(--saffron)]/40 bg-[linear-gradient(120deg,#2a0f04_0%,#4a1703_45%,#7a2a06_100%)] px-6 py-10 shadow-[0_24px_60px_-28px_rgba(122,42,6,0.8)] md:px-12 md:py-12">
        <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(233,162,59,0.28),transparent_70%)]" />
        <div className="pointer-events-none absolute right-6 top-6 font-deva text-[9rem] leading-none text-white/5 select-none">ॐ</div>
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gold)]/50 bg-black/20 px-3 py-1 text-xs font-medium tracking-wide text-[var(--gold)]">
            <Crown className="h-3.5 w-3.5" /> {pick("पंचांग प्रीमियम", "Panchangam Premium")}
          </span>
          <h2 className="mt-4 max-w-2xl font-deva text-3xl text-[#FBEFD8] deva-lh md:text-4xl">
            {pick("आपके जन्म एवं स्थान पर आधारित वैयक्तिक पंचांग", "A Panchangam tuned to your stars & your exact location")}
          </h2>
          <ul className="mt-5 grid max-w-3xl gap-x-6 gap-y-2 sm:grid-cols-2">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#F0Dcc0]/90">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" /> {pick(f[1], f[0])}
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link to="/panchanga-premium" data-testid="premium-banner-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[var(--gold)] px-7 py-3.5 text-base font-semibold text-[#2a0f04] shadow-lg transition-transform hover:scale-[1.03] active:scale-100">
              <Crown className="h-5 w-5" />
              {pick(`प्रीमियम अनलॉक करें – ₹${price}/माह`, `Unlock Premium – ₹${price}/month`)}
            </Link>
            <span className="text-xs text-[#F0Dcc0]/70">{pick("ऑटो-रिन्यू · कभी भी रद्द करें", "Auto-renews · cancel anytime")}</span>
          </div>
          <p className="mt-5 flex items-center gap-1.5 text-xs text-[var(--gold)]/90" title={pick("स्विस एफेमेरिस + लाहिरी (चित्रपक्ष) अयनांश, निरयन (सायडरियल) राशिचक्र — आपके सटीक अक्षांश/देशांतर एवं समय-क्षेत्र हेतु स्थानीय सूर्योदय/सूर्यास्त पर आधारित गणना।", "Swiss Ephemeris + Lahiri (Chitrapaksha) ayanamsa, sidereal zodiac — computed for your exact latitude/longitude and time zone using local sunrise/sunset.")}>
            <Star className="h-3.5 w-3.5" /> {pick("संचालित — धर्मार्थ ज्योतिष इंजन द्वारा", "Powered by the Dharmartha Jyotish Engine")}
          </p>
        </div>
      </div>
    </section>
  );
}
